<script setup>
import { ref, computed, toRef, onMounted } from 'vue';

import T from './JobagentCard.i18n.js';

import clamp from 'clamp-js-main';

import DsFoldableDataCard from 'JIX/components/ds/DsFoldableDataCard.vue';
import DsIconButton from 'JIX/components/ds/DsIconButton.vue';
import SentenceList from 'JIX/components/SentenceList.vue';
import SvgIcon from 'JIX/components/SvgIcon.vue';

import { bsTooltip as vBsTooltip } from 'JIX/vue-bs-tooltip.js';

import 'icons/edit--filled.svg';
import 'icons/notification-on.svg';
import 'icons/notification-off.svg';
import 'icons/tooltip.svg';

const props = defineProps({
    jobagent: {
        type: Object,
        required: true,
    },
});
const emit = defineEmits(['edit', 'toggle-activate', 'toggle-frequency', 'edit', 'delete']);

const card = ref();
const headline = ref();

const menu = computed(() => {
    return {
        id: "jobagent-menu-" + props.jobagent.id,
        items: [
            {
                key: 'toggle',
                label: props.jobagent.enabled ? T.deactivate() : T.activate(),
                onclick: () => emit('toggle-activate'),
            },
            ...(props.jobagent.enabled ? [
                {
                    key: 'toggle-frequency',
                    label: props.jobagent.frequency === 'daily' ? T.switchWeekly() : T.switchDaily(),
                    onclick: () => emit('toggle-frequency'),
                },
            ] : []),
            {
                key: 'edit',
                label: T.edit(),
                onclick: () => emit('edit'),
            },
            {
                key: 'search',
                label: T.doSearch(),
                url: props.jobagent.url,
            },
            {
                key: 'history',
                label: T.history(),
                url: props.jobagent.history_url,
            },
            {
                key: 'delete',
                label: T.delete(),
                onclick: () => emit('delete'),
                danger: true,
            },
        ],
    };
});
const radiusAreaName = computed(() => {
    if (!props.jobagent.search_params.address || !props.jobagent.search_params.radius) {
        return null;
    }
    return T.radiusAddress(props.jobagent.search_params.radius, props.jobagent.search_params.address);
});
const categoryNames = computed(() => {
    return props.jobagent.data_names.categories.map(x => x.name);
});
const oldCategoryNames = computed(() => {
    return props.jobagent.data_names.old_categories.map(x => x.name);
});
const companyNames = computed(() => {
    return props.jobagent.data_names.companies.map(x => x.name);
});
const areaNames = computed(() => {
    const res = props.jobagent.data_names.areas.map(x => x.text);
    if (radiusAreaName.value) {
        res.push(radiusAreaName.value);
    }
    return res;
});
const hasNoParams = computed(() => {
    const p = props.jobagent.search_params;
    return Object.keys(p).every(k => Array.isArray(p[k]) && p[k].length === 0 || p[k] === null);
});
const frequencyDisplay = computed(() => {
    if (props.jobagent.frequency === 'daily') {
        return T.daily();
    } else if (props.jobagent.frequency === 'weekly') {
        return T.weekly();
    } else {
        console.warn(`Does not know how to represent frequency '${props.jobagent.frequency}'`);
        return props.jobagent.frequency;
    }
});
const stateClasses = computed(() => {
    return ['jobagent-card__state', { 'jobagent-card__state--enabled': props.jobagent.enabled } ];
});

onMounted(() => {
    clamp(headline.value, { clamp: 1 });
});

defineExpose({
    open() {
        card.value.setState(true);
    },
    close() {
        card.value.setState(false);
    },
    jobagentId: toRef(() => props.jobagent.id),
});
</script>

<template>
    <DsFoldableDataCard
        :class="{ 'jobagent-card': true, 'border': oldCategoryNames.length > 0,'border-warning': oldCategoryNames.length > 0 }"
        :menu="menu"
        :open-text="T.openCardText()"
        ref="card"
    >
        <template #header>
            <a :href="jobagent.url" class="stretched-link link-basic jobagent-card__name" ref="headline">{{ jobagent.display_name }}</a>
            <div class="jobagent-card__hitcount">
                {{ T.hitcount(jobagent.hitcount, jobagent.hitcount_formatted) }}
            </div>

            <div :class="stateClasses">
                <SvgIcon :name="jobagent.enabled ? 'notification-on' : 'notification-off'" :aria-label="jobagent.enabled ? T.active() : T.inactive()" />
                {{ jobagent.enabled ? frequencyDisplay : T.inactive() }}
            </div>
        </template>
        <template #body>
            <div class="text-sm">
                <p class="text-muted" v-if="hasNoParams">
                    {{ T.noSearchParams() }}
                </p>
                <dl v-else>
                    <template v-if="jobagent.search_params.q">
                        <dt>{{ T.keywords() }}</dt>
                        <dd class="text-muted">
                            {{ jobagent.search_params.q }}
                        </dd>
                    </template>

                    <template v-if="categoryNames.length > 0">
                        <dt>{{ T.categories() }}</dt>
                        <dd class="text-muted">
                            <SentenceList :items="categoryNames" />
                        </dd>
                    </template>

                    <template v-if="oldCategoryNames.length > 0">
                        <dt class="text-warning">
                            {{ T.oldCategories() }}
                            <SvgIcon
                                v-bs-tooltip="T.oldCategoryTooltip()"
                                name="tooltip"
                                size="inline"
                            />
                        </dt>
                        <dd class="text-muted">
                            <SentenceList :items="oldCategoryNames" />
                        </dd>
                    </template>

                    <template v-if="areaNames.length > 0">
                        <dt>{{ T.areas() }}</dt>
                        <dd class="text-muted">
                            <SentenceList :items="areaNames" />
                        </dd>
                    </template>

                    <template v-if="companyNames.length > 0">
                        <dt>{{ T.companies() }}</dt>
                        <dd class="text-muted">
                            <SentenceList :items="companyNames" />
                        </dd>
                    </template>
                </dl>
            </div>

            <DsIconButton
                class="mt-3"
                type="primary"
                :label="T.edit()"
                icon-name="edit--filled"
                @click="$emit('edit')"
            />
        </template>
    </DsFoldableDataCard>
</template>
